import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../shared/Seo';

const FAQ_ITEMS = [
  {
    question: 'What is PonPon Gachapon?',
    answer:
      'PonPon offers authentic capsule toys from multiple series. The series is known, but the specific character you receive is random.',
  },
  {
    question: 'Do I receive real items?',
    answer:
      'Yes. Online gachapon spins result in physical collectibles that are shipped after purchase.',
  },
  {
    question: 'What is the PonPon Locker System?',
    answer:
      'The Locker System is a free feature for all registered members! After each spin, your item is automatically stored in your Locker instead of being shipped right away. Your Locker can hold up to 8 items, creating a digital collection you can showcase to friends and family. Once your Locker is full, you can ship all 8 items to your home in one shipment for free!',
  },
  {
    question: 'How does shipping work with the Locker System?',
    answer:
      'We offer free standard shipping only when you decide to ship your entire Locker (all 8 items). If you want to ship items before your Locker is full, standard shipping fees will apply for that partial shipment.',
  },
  {
    question: 'What are my chances of getting a Secret or Rare item?',
    answer:
      'The thrill of the hunt is part of the fun! The distribution for each series is determined by the manufacturer and, when available, is shown on the product page. Each spin provides one randomly distributed item from the selected series.',
  },
  {
    question: 'Can I choose a specific style?',
    answer:
      'To preserve the excitement and fairness of the capsule toy experience, we do not allow the selection of specific common styles or characters. All capsule toys are sold as random items. However, we sometimes offer "Complete Sets" for purchase, which include one of every common style from a particular series.',
  },
  {
    question: "What if I receive a duplicate or an item I don't want?",
    answer:
      'Due to the random nature of capsule toys, receiving duplicates is a possibility. We do not offer exchanges or refunds for unwanted common styles. We encourage our community to trade with other collectors! You can join our official social media channels to connect with fellow fans and enthusiasts.',
  },
  {
    question: 'What is your return and refund policy?',
    answer:
      'All sales are final, including token purchases. We do not accept returns or offer refunds for reasons such as receiving an unwanted common style or duplicate items. Refunds are only available for items that arrive defective or damaged. Please see the next question and our full Terms of Service for details.',
  },
  {
    question: 'What if my item is damaged or defective?',
    answer:
      'Your satisfaction is our priority. If you receive a product with verifiable manufacturing defects (e.g., significant paint flaws, broken parts, missing pieces), please contact our customer service within 7 days of delivery. Include your order number and clear photos or videos of the unboxing and the defect. Once reviewed and validated, we will offer a replacement (if available) or a refund.',
  },
  {
    question: 'Do you ship internationally?',
    answer:
      'We currently ship within the United States only. PonPon is an online-only store, and international shipping is not yet available.',
  },
  {
    question: "I didn't receive my order/the tracking hasn't updated.",
    answer:
      'Please allow 1-3 business days for order processing and for your tracking information to update. If it has been longer than this, please contact us with your order number. For lost packages, please refer to our Terms of Service on our shipping and delivery policy.',
  },
  {
    question: 'Is online gachapon gambling?',
    answer: 'No. Each spin guarantees a physical item from the listed series.',
  },
  {
    question: 'How can I stay updated on new releases?',
    answer:
      'Follow PonPon! on our social media channels and subscribe to our newsletter on the website to be the first to hear about new series, restocks, and exclusive promotions!',
  },
];

const FAQPage = () => {
  const faqStructuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    }),
    []
  );

  useEffect(() => {
    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div className="flex flex-col gap-8 px-4 pt-4 pb-8 sm:px-0 sm:pt-0 sm:pb-16">
      <Seo
        title="FAQ | PonPon!"
        description="Learn how PonPon! handles Locker storage, shipping, restocks, and refunds so you can enjoy gachapon from home with confidence."
        canonical="https://ponpon.app/faq"
        jsonLd={faqStructuredData}
      />
      <header>
        <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">FAQ</h1>
        <p className="mt-2 text-sm text-ponpon-navy/70">
          Answers to the most common questions from the PonPon community.
        </p>
      </header>

      <div className="space-y-5">
        {FAQ_ITEMS.map(({ question, answer }) => (
          <article
            key={question}
            className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">{question}</h2>
            <p className="mt-2 text-sm leading-relaxed text-ponpon-navy/75">{answer}</p>
          </article>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
        >
          <span aria-hidden="true">←</span>
          Back to homepage
        </Link>
      </div>
    </div>
  );
};

export default FAQPage;
